import { useState, useMemo, useEffect } from 'react';
import { useMutation, useQuery, useSubscription } from '@apollo/client/react';
import { gql } from '@apollo/client';
import { Button } from '../ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { RecordingModal } from './RecordingModal';
import { EditConferenceModal } from './EditConferenceModal';
import toast from 'react-hot-toast';
import { GET_CURRENT_USER } from '../../graphql/queries';
import { ON_WAITING_ROOM_UPDATED } from '../../graphql/videoConferences';

const START_VIDEO_CONFERENCE = gql`
  mutation StartVideoConference($id: ID!) {
    startVideoConference(id: $id) {
      meetingId
      attendeeId
      joinToken
    }
  }
`;

const CANCEL_VIDEO_CONFERENCE = gql`
  mutation CancelVideoConference($id: ID!, $reason: String) {
    cancelVideoConference(id: $id, reason: $reason) {
      id
      status
    }
  }
`;

interface Participant {
  id: string;
  type: 'CLIENT' | 'INQUIRY' | 'GUEST';
  referenceId?: string;
  name: string;
  email?: string;
  phone?: string;
}

interface WaitingRoomParticipant {
  id: string;
  name: string;
  participantType: string;
  requestedAt: string;
}

interface VideoConference {
  id: string;
  title: string;
  status: string;
  scheduledStartTime: string;
  scheduledEndTime: string;
  actualStartTime?: string;
  actualEndTime?: string;
  duration?: number;
  hostId: string;
  host?: {
    id: string;
    firstName: string;
    lastName: string;
  };
  // Legacy single participant fields
  guestName?: string;
  guestEmail?: string;
  clientId?: string;
  client?: {
    id: string;
    firstName: string;
    lastName: string;
  };
  inquiryId?: string;
  inquiry?: {
    id: string;
    firstName: string;
    lastName: string;
  };
  // New multi-participant field
  participants?: Participant[];
  isRecordingEnabled: boolean;
  recordingStatus?: string;
  joinUrl?: string;
  attendeeIds?: string[];
  waitingRoomParticipants?: WaitingRoomParticipant[];
}

interface VideoConferencesListProps {
  conferences: VideoConference[];
  loading: boolean;
  onRefetch: () => void;
  emptyMessage?: string;
  showScheduleButton?: boolean;
  onScheduleClick?: () => void;
}

// Filter options
type StatusFilter = 'ALL' | 'UPCOMING' | 'PAST' | 'CANCELLED';
type SortOrder = 'NEWEST' | 'OLDEST';

interface CurrentUserData {
  getCurrentUser: {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
    role: string;
  };
}

export function VideoConferencesList({
  conferences,
  loading,
  onRefetch,
  emptyMessage = 'No video conferences scheduled',
  showScheduleButton = false,
  onScheduleClick,
}: VideoConferencesListProps) {
  const [startConference, { loading: starting }] = useMutation(START_VIDEO_CONFERENCE);
  const [cancelConference, { loading: cancelling }] = useMutation(CANCEL_VIDEO_CONFERENCE);
  const [recordingModalId, setRecordingModalId] = useState<string | null>(null);
  const [editModalConferenceId, setEditModalConferenceId] = useState<string | null>(null);
  
  // Filter and sort state
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('ALL');
  const [sortOrder, setSortOrder] = useState<SortOrder>('NEWEST');

  // Get current user for permission checks
  const { data: userData } = useQuery<CurrentUserData>(GET_CURRENT_USER);
  const currentUser = userData?.getCurrentUser;

  // Track waiting room counts for each conference
  const [waitingRoomCounts, setWaitingRoomCounts] = useState<Record<string, number>>({});

  // Get scheduled conferences that need waiting room subscriptions
  const scheduledConferenceIds = useMemo(() => 
    conferences
      .filter(c => c.status === 'SCHEDULED' || c.status === 'IN_PROGRESS')
      .map(c => c.id),
    [conferences]
  );

  // Initialize waiting room counts from conference data
  useEffect(() => {
    const counts: Record<string, number> = {};
    conferences.forEach(conf => {
      if (conf.waitingRoomParticipants) {
        counts[conf.id] = conf.waitingRoomParticipants.length;
      }
    });
    setWaitingRoomCounts(prev => ({ ...prev, ...counts }));
  }, [conferences]);

  // Subscribe to waiting room updates for all scheduled conferences
  // We use a single subscription per conference that's active
  const activeConferenceId = scheduledConferenceIds[0]; // Subscribe to first active conference
  useSubscription<{ onWaitingRoomUpdated: { id: string; waitingRoomParticipants: WaitingRoomParticipant[] } }>(ON_WAITING_ROOM_UPDATED, {
    variables: { conferenceId: activeConferenceId },
    skip: !activeConferenceId,
    onData: ({ data: subData }) => {
      const update = subData?.data?.onWaitingRoomUpdated;
      if (update) {
        setWaitingRoomCounts(prev => ({
          ...prev,
          [update.id]: update.waitingRoomParticipants?.length || 0,
        }));
        // Refetch to get full updated data
        onRefetch();
      }
    },
  });

  // Helper to get waiting room count for a conference
  const getWaitingRoomCount = (conferenceId: string): number => {
    return waitingRoomCounts[conferenceId] || 0;
  };

  // Check if user can edit/cancel a conference (host or admin)
  const canManageConference = (conference: VideoConference): boolean => {
    if (!currentUser) return false;
    const isHost = conference.hostId === currentUser.id;
    const isAdmin = currentUser.role === 'ADMIN';
    return isHost || isAdmin;
  };

  // Check if current user is the host
  const isUserHost = (conference: VideoConference): boolean => {
    if (!currentUser) return false;
    return conference.hostId === currentUser.id;
  };

  // Get participants from conference (handles both legacy and new format)
  const getParticipants = (conference: VideoConference): { name: string; type: string }[] => {
    // New format: use participants array
    if (conference.participants && conference.participants.length > 0) {
      return conference.participants.map(p => ({
        name: p.name,
        type: p.type,
      }));
    }
    
    // Legacy format: construct from single-participant fields
    const participants: { name: string; type: string }[] = [];
    
    if (conference.client) {
      participants.push({
        name: `${conference.client.firstName} ${conference.client.lastName}`,
        type: 'CLIENT',
      });
    } else if (conference.inquiry) {
      participants.push({
        name: `${conference.inquiry.firstName} ${conference.inquiry.lastName}`,
        type: 'INQUIRY',
      });
    } else if (conference.guestName) {
      participants.push({
        name: conference.guestName,
        type: 'GUEST',
      });
    }
    
    return participants;
  };

  // Filter and sort conferences
  const filteredAndSortedConferences = useMemo(() => {
    let filtered = conferences.filter(conf => {
      switch (statusFilter) {
        case 'UPCOMING':
          return conf.status === 'SCHEDULED' || conf.status === 'IN_PROGRESS';
        case 'PAST':
          return conf.status === 'COMPLETED' || conf.status === 'NO_SHOW';
        case 'CANCELLED':
          return conf.status === 'CANCELLED';
        case 'ALL':
        default:
          return true;
      }
    });
    
    // Sort by scheduled start time
    filtered.sort((a, b) => {
      const dateA = new Date(a.scheduledStartTime).getTime();
      const dateB = new Date(b.scheduledStartTime).getTime();
      return sortOrder === 'NEWEST' ? dateB - dateA : dateA - dateB;
    });
    
    return filtered;
  }, [conferences, statusFilter, sortOrder]);

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      SCHEDULED: 'bg-blue-100 text-blue-700',
      IN_PROGRESS: 'bg-green-100 text-green-700',
      COMPLETED: 'bg-gray-100 text-gray-700',
      CANCELLED: 'bg-red-100 text-red-700',
      NO_SHOW: 'bg-yellow-100 text-yellow-700',
    };
    return colors[status] || 'bg-gray-100 text-gray-700';
  };

  const getStatusLabel = (status: string) => {
    const labels: Record<string, string> = {
      SCHEDULED: 'Scheduled',
      IN_PROGRESS: 'In Progress',
      COMPLETED: 'Completed',
      CANCELLED: 'Cancelled',
      NO_SHOW: 'No Show',
    };
    return labels[status] || status;
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatTime = (dateString: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  // Get initials from name
  const getInitials = (name: string): string => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  // Get avatar background color based on participant type
  const getAvatarColor = (type: string): string => {
    const colors: Record<string, string> = {
      CLIENT: 'bg-blue-500',
      INQUIRY: 'bg-purple-500',
      GUEST: 'bg-orange-500',
    };
    return colors[type] || 'bg-gray-500';
  };

  const handleStart = async (conferenceId: string) => {
    try {
      await startConference({ variables: { id: conferenceId } });
      toast.success('Conference started!');
      onRefetch();
      // Open video call in popup window
      const width = 1200;
      const height = 800;
      const left = (window.screen.width - width) / 2;
      const top = (window.screen.height - height) / 2;
      window.open(
        `/video-conferences/${conferenceId}`,
        'VideoCall',
        `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=no,status=no,toolbar=no,menubar=no,location=no`
      );
    } catch (error: any) {
      toast.error(error.message || 'Failed to start conference');
    }
  };

  const handleJoin = (conference: VideoConference) => {
    // Open video call in popup window
    const width = 1200;
    const height = 800;
    const left = (window.screen.width - width) / 2;
    const top = (window.screen.height - height) / 2;
    window.open(
      `/video-conferences/${conference.id}`,
      'VideoCall',
      `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=no,status=no,toolbar=no,menubar=no,location=no`
    );
  };

  const handleCancel = async (conferenceId: string) => {
    if (!confirm('Are you sure you want to cancel this conference?')) return;
    
    try {
      await cancelConference({ variables: { id: conferenceId } });
      toast.success('Conference cancelled');
      onRefetch();
    } catch (error: any) {
      toast.error(error.message || 'Failed to cancel conference');
    }
  };

  const handleEdit = (conferenceId: string) => {
    setEditModalConferenceId(conferenceId);
  };

  if (loading) {
    return (
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <svg className="w-5 h-5 text-aalaw-red" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
            </svg>
            Video Conferences
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-aalaw-red"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="flex items-center gap-2">
          <svg className="w-5 h-5 text-aalaw-red" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
          </svg>
          Video Conferences ({conferences.length})
        </CardTitle>
        {showScheduleButton && onScheduleClick && (
          <Button size="sm" onClick={onScheduleClick}>
            + Schedule
          </Button>
        )}
      </CardHeader>
      <CardContent>
        {/* Filter and Sort Controls */}
        {conferences.length > 0 && (
          <div className="flex flex-wrap items-center gap-4 mb-4 pb-4 border-b border-gray-200">
            {/* Status Filter */}
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-500">Filter:</span>
              <div className="flex gap-1">
                {(['ALL', 'UPCOMING', 'PAST', 'CANCELLED'] as StatusFilter[]).map((filter) => (
                  <button
                    key={filter}
                    onClick={() => setStatusFilter(filter)}
                    className={`px-3 py-1 text-sm rounded-full transition-colors ${
                      statusFilter === filter
                        ? 'bg-aalaw-red text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {filter === 'ALL' ? 'All' : filter.charAt(0) + filter.slice(1).toLowerCase()}
                  </button>
                ))}
              </div>
            </div>
            
            {/* Sort Order */}
            <div className="flex items-center gap-2 ml-auto">
              <span className="text-sm text-gray-500">Sort:</span>
              <select
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value as SortOrder)}
                className="px-3 py-1 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-aalaw-red focus:border-transparent"
              >
                <option value="NEWEST">Newest First</option>
                <option value="OLDEST">Oldest First</option>
              </select>
            </div>
          </div>
        )}

        {filteredAndSortedConferences.length === 0 ? (
          <div className="text-center py-8">
            <div className="text-4xl mb-3">📹</div>
            <p className="text-gray-500 text-sm mb-4">
              {conferences.length === 0 
                ? emptyMessage 
                : `No ${statusFilter.toLowerCase()} conferences found`}
            </p>
            {showScheduleButton && onScheduleClick && conferences.length === 0 && (
              <Button variant="outline" onClick={onScheduleClick}>
                Schedule Video Conference
              </Button>
            )}
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-1 lg:grid-cols-2">
            {filteredAndSortedConferences.map((conference) => {
              const participants = getParticipants(conference);
              const displayParticipants = participants.slice(0, 3);
              const remainingCount = participants.length - 3;
              const canManage = canManageConference(conference);
              const isHost = isUserHost(conference);

              return (
                <div
                  key={conference.id}
                  className="p-4 border border-gray-200 rounded-lg hover:shadow-md transition-shadow bg-white"
                >
                  {/* Card Header */}
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <h4 className="font-semibold text-gray-900 truncate">{conference.title}</h4>
                        <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${getStatusColor(conference.status)}`}>
                          {getStatusLabel(conference.status)}
                        </span>
                        {isHost && (
                          <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-aalaw-red/10 text-aalaw-red">
                            Host
                          </span>
                        )}
                      </div>
                    </div>
                    {conference.isRecordingEnabled && (
                      <span className="text-xs text-red-600 flex items-center gap-1 flex-shrink-0">
                        <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></span>
                        REC
                      </span>
                    )}
                  </div>

                  {/* Date/Time and Host */}
                  <div className="flex flex-col gap-2 mb-3 text-sm text-gray-600">
                    <div className="flex items-center gap-2">
                      <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      <span>{formatDate(conference.scheduledStartTime)}</span>
                      <span className="text-gray-400">•</span>
                      <span>{formatTime(conference.scheduledStartTime)} - {formatTime(conference.scheduledEndTime)}</span>
                    </div>
                    {conference.host && (
                      <div className="flex items-center gap-2">
                        <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                        <span>Host: {conference.host.firstName} {conference.host.lastName}</span>
                      </div>
                    )}
                  </div>

                  {/* Participant Avatars */}
                  {participants.length > 0 && (
                    <div className="flex items-center gap-2 mb-4">
                      <div className="flex -space-x-2">
                        {displayParticipants.map((participant, index) => (
                          <div
                            key={index}
                            className={`w-8 h-8 rounded-full ${getAvatarColor(participant.type)} flex items-center justify-center text-white text-xs font-medium border-2 border-white`}
                            title={`${participant.name} (${participant.type})`}
                          >
                            {getInitials(participant.name)}
                          </div>
                        ))}
                        {remainingCount > 0 && (
                          <div className="w-8 h-8 rounded-full bg-gray-300 flex items-center justify-center text-gray-600 text-xs font-medium border-2 border-white">
                            +{remainingCount}
                          </div>
                        )}
                      </div>
                      <span className="text-sm text-gray-500">
                        {participants.length} participant{participants.length !== 1 ? 's' : ''}
                      </span>
                    </div>
                  )}

                  {/* Waiting Room Indicator */}
                  {conference.status === 'SCHEDULED' && getWaitingRoomCount(conference.id) > 0 && (
                    <div className="flex items-center gap-2 mb-3 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg">
                      <svg className="w-4 h-4 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                      </svg>
                      <span className="text-sm font-medium text-amber-700">
                        {getWaitingRoomCount(conference.id)} {getWaitingRoomCount(conference.id) === 1 ? 'person' : 'people'} waiting to join
                      </span>
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="flex items-center gap-2 flex-wrap pt-3 border-t border-gray-100">
                    {conference.status === 'SCHEDULED' && (
                      <>
                        <Button
                          size="sm"
                          onClick={() => handleStart(conference.id)}
                          disabled={starting}
                          className="bg-green-600 hover:bg-green-700"
                        >
                          Start{getWaitingRoomCount(conference.id) > 0 ? ` (${getWaitingRoomCount(conference.id)} waiting)` : ''}
                        </Button>
                        {canManage && (
                          <>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleEdit(conference.id)}
                            >
                              Edit
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleCancel(conference.id)}
                              disabled={cancelling}
                              className="text-red-600 hover:bg-red-50"
                            >
                              Cancel
                            </Button>
                          </>
                        )}
                      </>
                    )}
                    {conference.status === 'IN_PROGRESS' && (
                      <Button
                        size="sm"
                        onClick={() => handleJoin(conference)}
                        className="bg-green-600 hover:bg-green-700"
                      >
                        Join
                      </Button>
                    )}
                    {conference.status === 'COMPLETED' && conference.recordingStatus === 'COMPLETED' && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setRecordingModalId(conference.id)}
                      >
                        View Recording
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>

      {/* Recording Modal */}
      <RecordingModal
        conferenceId={recordingModalId || ''}
        isOpen={!!recordingModalId}
        onClose={() => setRecordingModalId(null)}
      />

      {/* Edit Conference Modal */}
      {editModalConferenceId && currentUser && (
        <EditConferenceModal
          isOpen={!!editModalConferenceId}
          onClose={() => setEditModalConferenceId(null)}
          onUpdated={() => {
            setEditModalConferenceId(null);
            onRefetch();
          }}
          conferenceId={editModalConferenceId}
          currentUserId={currentUser.id}
          currentUserRole={currentUser.role}
        />
      )}
    </Card>
  );
}
